import React, { useState, useEffect } from "react";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import Termsandcondition from "../../components/common/t&c/Termsandcondition";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { useLoginInstance } from "../../utils/userInstance/useLoginInstace";
import { useSelector } from "react-redux";
import { FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
    const navigate = useNavigate();
    const { isUsermanagementLoader } = useSelector(state => state.loader);
    const { login, isErrors } = useLoginInstance({});
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [hashedPassword, setHashedPassword] = useState("");
    const [errors, setErrors] = useState({
        email: "", 
        password: ""
    });

    const [showPassword, setShowPassword] = useState(false);

    // Enhanced OTP detection and auto-redirect (similar to signup flow)
    useEffect(() => {
        if (isErrors) {
            console.log('Login Error Response:', isErrors);
            
            // Convert error response to string for pattern matching
            const errorString = JSON.stringify(isErrors).toLowerCase();
            
            // Comprehensive OTP detection patterns
            const otpPatterns = [
                "otp sent to your mail since its been a long time",
                "otp sent to your email",
                "verification code sent",
                "please verify your email", 
                "check your email for verification",
                "two factor authentication required",
                "otp verification required",
                "email verification needed",
                "verify your account"
            ];
            
            // Check for specific OTP messages
            const hasOtpMessage = otpPatterns.some(pattern => 
                errorString.includes(pattern.toLowerCase())
            );
            
            // Check for OTP keywords in error properties
            const otpKeywords = ['otp', 'verification', 'verify', 'code', 'two-factor', '2fa'];
            const hasOtpKeyword = otpKeywords.some(keyword => 
                errorString.includes(keyword)
            );
            
            // Check all possible error object structures
            const errorSources = [
                isErrors?.message,
                isErrors?.error, 
                isErrors?.label,
                isErrors?.data?.message,
                isErrors?.response?.data?.message,
                isErrors?.response?.message,
                isErrors?.data?.error,
                isErrors?.errorMessage,
                isErrors?.details?.message
            ];
            
            const hasOtpInErrorSources = errorSources.some(source => 
                source && typeof source === 'string' && source.toLowerCase().includes('otp')
            );
            
            // Trigger OTP redirect if any condition is met
            if (hasOtpMessage || hasOtpKeyword || hasOtpInErrorSources) {
                console.log('OTP verification required - redirecting...');
                handleOtpRedirect();
                return;
            }
        }
    }, [isErrors, navigate, email]);

    // Handle OTP redirect (following signup pattern)
    const handleOtpRedirect = () => {
        // Store data for OTP verification page (same as signup flow)
        sessionStorage.setItem('verificationEmail', email);
        sessionStorage.setItem('otpRequestTime', new Date().toISOString());
        sessionStorage.setItem('fromLogin', 'true'); // Distinguish from signup
        
        // Store error message for display
        const errorMessage = extractErrorMessage();
        if (errorMessage) {
            sessionStorage.setItem('otpMessage', errorMessage);
        }
        
        // Clear form errors to prevent loops
        setErrors({ email: "", password: "" });
        
        // Navigate to verification page (same route as signup)
        navigate('/verify', { 
            state: { 
                email: email,
                fromLogin: true,
                message: errorMessage || 'OTP verification required. Please check your email.'
            },
            replace: true // Replace current history entry
        });
    };

    // Extract error message from response
    const extractErrorMessage = () => {
        if (!isErrors) return null;
        
        // Priority order for extracting message
        const messageSources = [
            isErrors?.message,
            isErrors?.data?.message,
            isErrors?.response?.data?.message,
            isErrors?.error,
            isErrors?.errorMessage,
            isErrors?.label
        ];
        
        for (const source of messageSources) {
            if (source && typeof source === 'string') {
                return source;
            }
        }
        
        return null;
    };

    /* ───────────────── password hash helper (same as signup) ───────────────── */
    const generatePasswordHash = (password) => {
        const rawKey = "A7VCsRP/mkFmGQrycUixJ26dQ6oft0HymEfocEJjIoI=";
        const keyBytes = CryptoJS.enc.Base64.parse(rawKey);
        const passwordBytes = CryptoJS.enc.Utf8.parse(password);
        const combinedBytes = passwordBytes.clone().concat(keyBytes);
        const sha256Hash = CryptoJS.SHA256(combinedBytes);
        return CryptoJS.enc.Base64.stringify(sha256Hash);
    }

    /* ───────────────── input change (same pattern as signup) ───────────────── */
    const handleInputChange = (e, field) => {
        const { value } = e.target;

        if (field === "email") {
            setEmail(value);
            setErrors({ ...errors, email: "" });
        }

        if (field === "password") {
            setPassword(value);
            setErrors({ ...errors, password: "" });
        }
    };

    /* ───────────────── form validation (same pattern as signup) ───────────────── */
    const validateForm = () => {
        const formErr = { email: "", password: "" };
        let isValid = true;

        // Email validation
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            formErr.email = "Please enter a valid email address.";
            isValid = false;
        }

        // Password validation
        if (!password) {
            formErr.password = "Please enter a valid password.";
            isValid = false;
        }

        setErrors(formErr);
        return isValid;
    };

    /* ───────────────── submit (following signup pattern) ───────────────── */
    const handleLogin = (e) => {
        e.preventDefault();
        
        if (validateForm()) {
            console.log("Attempting login with email:", email);
            
            const encryptedPassword = generatePasswordHash(password);
            setHashedPassword(encryptedPassword);
            
            // Clear any previous OTP-related session data
            sessionStorage.removeItem('otpMessage');
            sessionStorage.removeItem('otpRequestTime');
            sessionStorage.removeItem('fromLogin');
            
            // Call login function (same pattern as signup)
            login({ 
                email: email, 
                password: encryptedPassword 
            });
        }
    };

    /* ───────────────── navigation helpers ───────────────── */
    const handleSignup = () => {
        navigate('/signup');
    };

    const handleForgotPassword = () => {
        navigate('/forgotpassword');
    };

    // Filter out OTP-related errors from form display
    const getDisplayError = (errorField) => {
        const error = errors[errorField] || isErrors?.[errorField];
        if (!error) return "";
        
        const errorString = error.toString().toLowerCase();
        const otpKeywords = ['otp', 'verification', 'verify', 'code'];
        
        // Don't display OTP-related errors since we auto-redirect
        if (otpKeywords.some(keyword => errorString.includes(keyword))) {
            return "";
        }
        
        return error;
    };

    /* ───────────────── JSX (same structure as signup) ───────────────── */
    return (
        <div className="bg-grey-200 h-screen w-full lg:w-4/4 flex flex-col justify-center items-center p-5 login-page">
            <div className="w-full min-w-[200px] xs:max-w-[320px] sm:max-w-[420px]">
                <div>
                    <p className="text-2xl font-bold text-center lg:text-start">Welcome back!</p>
                    <p className="text-sm font-medium mt-2 text-center lg:text-start text-gray-500">
                        New to Campus Crow?{" "}
                        <span
                            className="text-primary underline cursor-pointer"
                            onClick={handleSignup}
                        >
                            Sign up
                        </span>
                    </p>
                </div>

                <form onSubmit={handleLogin} className="mt-5">
                    {/* EMAIL */}
                    <div className="mt-3">
                        <Input
                            type="text"
                            placeholder="Enter your email"
                            required={true}
                            label="Email"
                            value={email}
                            onChange={(e) => handleInputChange(e, "email")}
                            error={getDisplayError("email")}
                            fullWidth={true}
                        />
                    </div>

                    {/* PASSWORD */}
                     <div className="mt-3 relative">
                        <Input
                            type={showPassword ? "text" : "password"} // Toggle type
                            placeholder="Enter your password"
                            required={true}
                            label="Password"
                            value={password}
                            onChange={(e) => handleInputChange(e, "password")}
                            error={getDisplayError("password")}
                            fullWidth={true}
                        />
                        <div 
                            className="absolute right-3 top-9 cursor-pointer text-gray-500 hover:text-gray-700"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                        </div>
                    </div>

                    <div className="mt-1">
                        <p
                            className="font-medium text-primary hover:underline text-end cursor-pointer"
                            onClick={handleForgotPassword}
                        >
                            Forgot password?
                        </p>
                    </div>

                    {/* SUBMIT BUTTON (same pattern as signup) */}
                    <div className="mt-8">
                        <Button
                            type="submit"
                            label={
                                isUsermanagementLoader ? (
                                    <div className="flex justify-center">
                                        <svg
                                            className="animate-spin h-6 w-6 text-white"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                    </div>
                                ) : (
                                    "Login"
                                )
                            }
                            varient="contained"
                            color="primary"
                            fullWidth={true}
                            disabled={isUsermanagementLoader}
                        />
                    </div>
                </form>

                <div className="mt-4">
                    <Button
                        label="← Back to Home"
                        varient="outlined"
                        color="primary"
                        fullWidth={true}
                        onClick={() => navigate("/")}
                    />
                </div>

                <Termsandcondition />
            </div>
        </div>
    );
};

export default Login;